import { MultilingualField } from './localization';

// User Status
export type UserStatus = 'active' | 'inactive' | 'suspended' | 'pending';

// User Roles
//
// MVP tenant-visible roles: OWNER / DOCTOR / NURSE / RECEPTIONIST /
// BILLING. Legacy enum values (PHARMACIST, RADIOLOGIST, …) are kept so
// existing call sites still compile, but they should be treated as
// deprecated and removed once the migration backfill remaps every
// membership to the MVP set. SUPER_ADMIN / IT_SUPPORT are platform-only
// and never surface in tenant-facing UI.
export enum UserRole {
  OWNER = 'owner',
  CLINIC_MANAGER = 'clinic_manager',
  DOCTOR = 'physician', // value kept to match legacy backend tokens
  NURSE = 'nurse',
  RECEPTIONIST = 'receptionist',
  BILLING = 'billing',

  SUPER_ADMIN = 'super_admin',
  IT_SUPPORT = 'it_support',

  // Legacy — pending UI migration.
  ADMIN = 'admin',
  PHARMACIST = 'pharmacist',
  LAB_TECH = 'lab_tech',
  RADIOLOGIST = 'radiologist',
  BILLING_STAFF = 'billing_staff',
  SECURITY = 'security',
  DEPARTMENT_HEAD = 'department_head',
  CASE_MANAGER = 'case_manager',
  SOCIAL_WORKER = 'social_worker',
}

// Permissions - Granular access control
export enum Permission {
  // Patient Management
  PATIENT_READ = 'patient:read',
  PATIENT_WRITE = 'patient:write',
  PATIENT_DELETE = 'patient:delete',
  PATIENT_ADMIN = 'patient:admin',

  // Medical Records.
  //
  // The backend resource is `medical_records` (see ROLE_DEFINITIONS in
  // permission-sync.service.ts), so the `record:*` strings below are granted
  // to NO role — they silently match nothing. Gating on them hid the
  // Referrals nav entry from every user, and three of the four Pharmacy tabs
  // (including Refills, which the sidebar badge counts). Use the
  // MEDICAL_RECORDS_* pair; the `record:*` members are kept only because the
  // locale files key role-management labels off their names.
  MEDICAL_RECORDS_READ = 'medical_records:read',
  MEDICAL_RECORDS_WRITE = 'medical_records:write',
  /** @deprecated Matches no backend permission. Use MEDICAL_RECORDS_READ. */
  RECORD_READ = 'record:read',
  /** @deprecated Matches no backend permission. Use MEDICAL_RECORDS_WRITE. */
  RECORD_WRITE = 'record:write',
  /** @deprecated Matches no backend permission. */
  RECORD_DELETE = 'record:delete',
  /** @deprecated Matches no backend permission. */
  RECORD_ADMIN = 'record:admin',

  // Referral letters — authored and signed by prescribers, read by
  // managers/nurses. The Referrals page was gated on the dead `record:read`
  // and was therefore unreachable from the sidebar for everyone.
  REFERRAL_READ = 'referral:read',
  REFERRAL_WRITE = 'referral:write',

  // Appointments
  APPOINTMENT_READ = 'appointment:read',
  APPOINTMENT_WRITE = 'appointment:write',
  APPOINTMENT_DELETE = 'appointment:delete',
  APPOINTMENT_ADMIN = 'appointment:admin',

  // Provider Availability — distinct from appointments because it's
  // who-is-on-shift, not which-slot-is-booked. Backend grants Doctors
  // read+write, Nurses read-only, Receptionists full, Billing none.
  AVAILABILITY_READ = 'availability:read',
  AVAILABILITY_WRITE = 'availability:write',
  AVAILABILITY_DELETE = 'availability:delete',
  AVAILABILITY_ADMIN = 'availability:admin',

  // Lab Orders
  LAB_ORDER_READ = 'lab_order:read',
  LAB_ORDER_WRITE = 'lab_order:write',
  LAB_ORDER_DELETE = 'lab_order:delete',
  LAB_ORDER_RESULT = 'lab_order:result',
  LAB_ORDER_ADMIN = 'lab_order:admin',

  // CPOE (Orders)
  ORDER_READ = 'order:read',
  ORDER_WRITE = 'order:write',
  ORDER_DELETE = 'order:delete',
  ORDER_APPROVE = 'order:approve',
  ORDER_ADMIN = 'order:admin',

  // Pharmacy
  PHARMACY_READ = 'pharmacy:read',
  PHARMACY_WRITE = 'pharmacy:write',
  PHARMACY_VERIFY = 'pharmacy:verify',
  PHARMACY_DISPENSE = 'pharmacy:dispense',
  PHARMACY_ADMIN = 'pharmacy:admin',

  // ADT (Admission/Discharge/Transfer)
  ADT_READ = 'adt:read',
  ADT_WRITE = 'adt:write',
  ADT_ADMIT = 'adt:admit',
  ADT_DISCHARGE = 'adt:discharge',
  ADT_TRANSFER = 'adt:transfer',
  ADT_ADMIN = 'adt:admin',

  // Billing & Revenue
  BILLING_READ = 'billing:read',
  BILLING_WRITE = 'billing:write',
  BILLING_PROCESS = 'billing:process',
  BILLING_ADMIN = 'billing:admin',

  // Reporting & Analytics
  REPORT_READ = 'report:read',
  REPORT_WRITE = 'report:write',
  REPORT_ADMIN = 'report:admin',

  // Administration
  USER_READ = 'user:read',
  USER_WRITE = 'user:write',
  USER_DELETE = 'user:delete',
  USER_ADMIN = 'user:admin',

  ROLE_READ = 'role:read',
  ROLE_WRITE = 'role:write',
  ROLE_DELETE = 'role:delete',
  ROLE_ADMIN = 'role:admin',

  SYSTEM_READ = 'system:read',
  SYSTEM_WRITE = 'system:write',
  SYSTEM_ADMIN = 'system:admin',

  // System Monitoring & Configuration
  SYSTEM_MONITORING = 'system:monitoring',
  SYSTEM_CONFIG = 'system:config',
  SYSTEM_BACKUP = 'system:backup',
  SYSTEM_RESTORE = 'system:restore',

  // Hospital Settings & Branding
  HOSPITAL_SETTINGS_READ = 'hospital_settings:read',
  HOSPITAL_SETTINGS_WRITE = 'hospital_settings:write',
  BRANDING_CONFIG = 'branding:config',

  // Audit & Security
  AUDIT_READ = 'audit:read',
  AUDIT_ADMIN = 'audit:admin',
  AUDIT_LOGS_READ = 'audit_logs:read',
  AUDIT_LOGS_EXPORT = 'audit_logs:export',
  AUDIT_REPORTS_GENERATE = 'audit_reports:generate',

  // Templates
  TEMPLATE_READ = 'template:read',
  TEMPLATE_WRITE = 'template:write',
  TEMPLATE_DELETE = 'template:delete',
  TEMPLATE_ADMIN = 'template:admin',

  // Imaging Orders
  IMAGING_ORDER_READ = 'imaging_order:read',
  IMAGING_ORDER_WRITE = 'imaging_order:write',
  IMAGING_ORDER_DELETE = 'imaging_order:delete',

  // Expenses and payroll — the accountant's two verbs
  // `expense:*` is deliberately not `billing:*`: reading the books is not the
  // power to take a patient's payment at the desk. `payout:approve` is split
  // from write because the person who assembles a payroll run must not be the
  // one who releases the money.
  EXPENSE_READ = 'expense:read',
  EXPENSE_WRITE = 'expense:write',
  EXPENSE_DELETE = 'expense:delete',
  PAYOUT_READ = 'payout:read',
  PAYOUT_WRITE = 'payout:write',
  PAYOUT_APPROVE = 'payout:approve',

  // Stock / the shelf
  // `stock:read` is deliberately wider than write: a nurse checks whether the
  // drops are there before a round, but never receives or writes off.
  STOCK_READ = 'stock:read',
  STOCK_WRITE = 'stock:write',

  // Medications / Clinic Formulary
  // Distinct from `prescription:*` — formulary governance is an admin
  // concern. `medication:recall` is its own action because a recall is
  // a clinic-wide safety event.
  MEDICATION_READ = 'medication:read',
  MEDICATION_WRITE = 'medication:write',
  MEDICATION_RECALL = 'medication:recall',
  MEDICATION_DELETE = 'medication:delete',
}

// Department definitions
export interface Department {
  id: string;
  name: MultilingualField;
  code: string;
  description?: MultilingualField;
  headOfDepartment?: string; // User ID
  location?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Permission object as returned by backend
export interface PermissionObject {
  id: string;
  name: string;
  resource: string;
  action: string;
  description?: string;
}

// Role definition with permissions (matches backend structure)
export interface Role {
  id: string;
  name: string;
  displayName: string; // Backend returns string, not MultilingualField
  description?: string; // Backend returns string, not MultilingualField
  permissions: PermissionObject[]; // Backend returns full permission objects
  inheritsFrom?: string[]; // Role IDs to inherit permissions from
  isSystemRole: boolean; // Cannot be deleted
  // isActive is not returned by backend, so removed
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// User interface
export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  avatar?: string;
  phoneNumber?: string;
  /**
   * True when this user signed up via SSO and the backend minted a
   * random placeholder password they don't know. Clinician step-up
   * surfaces (e.g. Sign Record) branch on this to skip the password
   * prompt and route the user to "set a real password first" instead.
   */
  passwordIsPlaceholder?: boolean;

  // Role and permissions
  primaryRole: UserRole;
  additionalRoles?: UserRole[];
  customPermissions?: Permission[]; // Additional permissions beyond role
  departmentIds: string[];

  // Professional information
  licenseNumber?: string;
  npiNumber?: string; // National Provider Identifier
  deaNumber?: string; // For prescribing
  specialties?: string[];
  /**
   * Primary specialty — drives default SOAP prompt, hotword pack, and
   * template/protocol suggestions. Validated server-side against the
   * canonical SPECIALTIES list; null means "not yet picked" and the EMR
   * surfaces a one-time prompt to fill it in.
   */
  specialty?: string | null;
  /** Free-text refinement under the primary specialty (e.g. "Interventional cardiology"). */
  subspecialty?: string | null;
  title?: string;

  // Employment information
  employeeId?: string;
  hireDate?: string;
  isFullTime: boolean;
  workSchedule?: WorkSchedule;

  // Security and access
  status: UserStatus;
  lastLogin?: string;
  lastPasswordChange?: string;
  passwordExpiresAt?: string;
  failedLoginAttempts: number;
  lockedUntil?: string;
  requirePasswordChange: boolean;
  twoFactorEnabled: boolean;

  // Preferences
  language: 'en' | 'ar';
  timezone: string;
  theme?: string;
  preferences?: UserPreferences;

  // Audit fields
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastModifiedBy: string;
}

// Work schedule
export interface WorkSchedule {
  monday?: TimeSlot[];
  tuesday?: TimeSlot[];
  wednesday?: TimeSlot[];
  thursday?: TimeSlot[];
  friday?: TimeSlot[];
  saturday?: TimeSlot[];
  sunday?: TimeSlot[];
}

export interface TimeSlot {
  startTime: string; // HH:mm format
  endTime: string;
  breakStart?: string;
  breakEnd?: string;
}

// User preferences
export interface UserPreferences {
  notifications: {
    email: boolean;
    sms: boolean;
    inApp: boolean;
    criticalAlerts: boolean;
    labResults: boolean;
    appointmentReminders: boolean;
  };
  dashboard: {
    defaultView: string;
    widgets: string[];
  };
  clinical: {
    defaultTemplates: string[];
    autoSave: boolean;
    autoSaveInterval: number; // minutes
  };
}

// Authentication related types
export interface LoginCredentials {
  username: string;
  password: string;
  rememberMe?: boolean;
  twoFactorCode?: string;
}

export interface AuthSession {
  user: User;
  token: string;
  refreshToken: string;
  expiresAt: string;
  permissions: Permission[];
  departments: Department[];
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  preventReuse: number; // Number of previous passwords to check
  expirationDays: number;
  lockoutAttempts: number;
  lockoutDuration: number; // minutes
}

// User session and activity
export interface UserSession {
  id: string;
  userId: string;
  deviceInfo: {
    userAgent: string;
    ip: string;
    location?: string;
    deviceType: 'desktop' | 'mobile' | 'tablet';
  };
  loginTime: string;
  lastActivity: string;
  isActive: boolean;
}

export interface UserActivity {
  id: string;
  userId: string;
  action: string;
  resource: string;
  resourceId?: string;
  details?: Record<string, unknown>;
  timestamp: string;
  ipAddress: string;
  userAgent: string;
}

// Role templates for quick setup
export interface RoleTemplate {
  role: UserRole;
  permissions: Permission[];
  description: string;
}

// Permission groups for easier management
export interface PermissionGroup {
  name: string;
  displayName: MultilingualField;
  permissions: Permission[];
  description?: MultilingualField;
}

// User creation/edit forms
export interface CreateUserRequest {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  primaryRole: UserRole;
  departmentIds: string[];
  temporaryPassword?: string;
  sendWelcomeEmail: boolean;
  phoneNumber?: string;
  title?: string;
  licenseNumber?: string;
  npiNumber?: string;
  specialties?: string[];
  workSchedule?: WorkSchedule;
  // Optional fields the API layer maps onto the backend CreateUserDto.
  password?: string;
  status?: UserStatus;
}

export interface UpdateUserRequest {
  username?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  primaryRole?: UserRole;
  additionalRoles?: UserRole[];
  departmentIds?: string[];
  status?: UserStatus;
  title?: string;
  specialties?: string[];
  workSchedule?: WorkSchedule;
  preferences?: Partial<UserPreferences>;
  // Security/account fields updatable through the same path.
  lastPasswordChange?: string;
  requirePasswordChange?: boolean;
  failedLoginAttempts?: number;
}

// User search and filtering
export interface UserSearchFilters {
  query?: string;
  role?: UserRole[];
  department?: string[];
  status?: UserStatus[];
  isActive?: boolean;
  hasPermission?: Permission[];
}

export interface UserListItem {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  primaryRole: UserRole;
  departments: Department[];
  status: UserStatus;
  lastLogin?: string;
  avatar?: string;
}